import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Scale, Star, Trash2, Award, TrendingUp, Leaf } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function StarRating({ value, max = 5 }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: max }, (_, i) => (
        <Star
          key={i}
          size={14}
          fill={i < value ? '#F59E0B' : 'none'}
          className={i < value ? 'text-yellow-400' : 'text-emerald-100/20'}
        />
      ))}
    </div>
  );
}

function StatCard({ icon, label, value, unit, color }) {
  return (
    <div className="card-grow p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(16,185,129,0.12)' }}>
          {icon}
        </div>
        <div>
          <p className="font-heading text-2xl font-bold text-emerald-50">
            {value != null ? value : '–'}
            {value != null && unit && <span className="text-base font-normal text-emerald-100/50 ml-1">{unit}</span>}
          </p>
          <p className="text-xs text-emerald-100/50 mt-0.5">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function HarvestsPage() {
  const [harvests, setHarvests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [hRes, sRes] = await Promise.all([
          axios.get(`${API}/api/harvests`),
          axios.get(`${API}/api/harvests/stats`),
        ]);
        setHarvests(Array.isArray(hRes.data) ? hRes.data : []);
        setStats(sRes.data);
      } catch { setHarvests([]); }
      setLoading(false);
    };
    load();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Ernte-Eintrag löschen?')) return;
    try {
      await axios.delete(`${API}/api/harvests/${id}`);
      setHarvests(prev => prev.filter(h => h.id !== id));
    } catch {}
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-6 animate-fade-in" data-testid="harvests-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-emerald-50">Ernte-Tracking</h1>
        <p className="text-emerald-100/50 text-sm mt-0.5">Auswertung deiner Ernten</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="harvest-stats">
        <StatCard
          icon={<Award size={20} className="text-emerald-400" />}
          label="Ernten gesamt"
          value={stats?.total_harvests ?? 0}
        />
        <StatCard
          icon={<Scale size={20} className="text-emerald-400" />}
          label="Trockengewicht gesamt"
          value={stats?.total_dry_weight_g != null && stats.total_dry_weight_g > 0
            ? stats.total_dry_weight_g >= 1000
              ? (stats.total_dry_weight_g / 1000).toFixed(2)
              : stats.total_dry_weight_g.toFixed(1)
            : null}
          unit={stats?.total_dry_weight_g >= 1000 ? 'kg' : 'g'}
        />
        <StatCard
          icon={<TrendingUp size={20} className="text-emerald-400" />}
          label="Ø Qualität"
          value={stats?.avg_quality ? `${stats.avg_quality} / 5` : null}
        />
      </div>

      {/* Harvest List */}
      {harvests.length === 0 ? (
        <div className="text-center py-16" data-testid="no-harvests">
          <Scale size={48} className="mx-auto text-emerald-500/30 mb-4" />
          <p className="text-emerald-100/50 mb-2">Noch keine Ernten erfasst</p>
          <p className="text-sm text-emerald-100/30">
            Setze eine Pflanze auf Phase "Ernte" und erfasse die Ernte auf der Pflanzenseite.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="harvests-list">
          {harvests.map(h => (
            <div key={h.id} className="card-grow p-4" data-testid={`harvest-${h.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-emerald-400 flex-shrink-0"
                    style={{ background: 'rgba(16,185,129,0.1)' }}>
                    <Leaf size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-semibold text-emerald-100">{h.plant_name}</h3>
                      {h.strain_name && (
                        <span className="badge badge-purple">{h.strain_name}</span>
                      )}
                    </div>
                    <p className="text-xs text-emerald-100/40 mt-0.5">
                      {h.harvest_date ? new Date(h.harvest_date).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }) : '–'}
                    </p>

                    {/* Weight Data */}
                    <div className="flex flex-wrap gap-4 mt-3">
                      {h.wet_weight_g != null && (
                        <div>
                          <p className="text-xs text-emerald-100/40 uppercase tracking-wider">Nassgewicht</p>
                          <p className="font-heading font-semibold text-emerald-200">
                            {h.wet_weight_g >= 1000 ? `${(h.wet_weight_g / 1000).toFixed(2)} kg` : `${h.wet_weight_g} g`}
                          </p>
                        </div>
                      )}
                      {h.dry_weight_g != null && (
                        <div>
                          <p className="text-xs text-emerald-100/40 uppercase tracking-wider">Trockengewicht</p>
                          <p className="font-heading font-semibold text-emerald-400">
                            {h.dry_weight_g >= 1000 ? `${(h.dry_weight_g / 1000).toFixed(2)} kg` : `${h.dry_weight_g} g`}
                          </p>
                        </div>
                      )}
                      {h.wet_weight_g && h.dry_weight_g && (
                        <div>
                          <p className="text-xs text-emerald-100/40 uppercase tracking-wider">Ausbeute</p>
                          <p className="font-heading font-semibold text-yellow-400">
                            {((h.dry_weight_g / h.wet_weight_g) * 100).toFixed(0)}%
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Quality */}
                    {h.quality != null && (
                      <div className="flex items-center gap-2 mt-2">
                        <p className="text-xs text-emerald-100/40 uppercase tracking-wider">Qualität:</p>
                        <StarRating value={h.quality} />
                      </div>
                    )}

                    {/* Notes */}
                    {h.notes && (
                      <p className="text-sm text-emerald-100/50 mt-2 italic">"{h.notes}"</p>
                    )}
                  </div>
                </div>

                <button onClick={() => handleDelete(h.id)}
                  className="p-1.5 rounded text-emerald-100/30 hover:text-red-400 transition-colors flex-shrink-0"
                  data-testid={`delete-harvest-${h.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
